import {useQuery, gql} from "@apollo/client";
import {useContext, useEffect, useMemo} from "react";
import {LocaleContext} from "../store/providers/LocaleProvider";
import {useLogger} from "./useLogger";
import {ResortsContext} from "../store/providers/ResortsProvider";

const QUERY = gql`
    query getResortDetailBySlug($slug: String!, $locale: String!) {
        resortCollection(limit: 1, where: { slug: $slug }, locale: $locale) {
            items {
                hapimagResortId
                isOnlineBookable
                resortName
                slug
                tourismTax
                houseboat
                resortCardHelperText
                destination{
                    destination
                    slug
                    country{
                        country
                        slug
                    }
                }
                resortInspirationImagesCollection {
                    items {
                        url
                    }
                }
                surroundings {
                    title
                    surroundingTipp
                    surroundingDescription
                    nearbyCollection(limit: 6) {
                        total
                        items {
                            placeClassification{
                                text,
                                isStandaloneName
                            }
                            name
                            givenName
                            distanceToTheResort
                            distanceInMinutes
                            relatedMeansOfTransport {
                                iconId,
                                recommendation
                            }
                        }
                    }
                }
                sys {
                    id
                }
                resortApartmentAmenities {
                    sys {
                        id
                    }
                }
                arrivalLocation {
                    tipp
                }
                resortPositioningCollection {
                    items {
                        title
                    }
                }
                payment {
                    paymentTitle
                    paymentInformation
                    paymentInformationWithPointsPurchase
                }
                resortInfo {
                    checkInTime
                    checkOutTime
                    openingHoursCollection {
                        items {
                            weekdays
                            openingHoursMorningFrom
                            openingHoursMorningTo
                            openingHoursAfternoonFrom
                            openingHoursAfternoonTo
                            allDayOpen24H
                        }
                    }
                    receptionInfo{
                        informationDisplayed
                    }
                    lateCheckIn {
                        informationDisplayed
                    }
                    petsInformation {
                        informationDisplayed
                    }
                    smokingInformation {
                        informationDisplayed
                    }
                    additionalInfo {
                        json
                    }
                    cancellationPolicy {
                        informationDisplayed {
                            json
                        }
                    }
                    cancellationPolicyLearnMoreLink
                    accessibilityInformation {
                        informationDisplayed
                    }
                }
                closingTimesCollection{
                    items{
                        from
                        to
                        information
                        datePickerCta{
                            url
                            label
                        }
                    }
                }
                resortContactDetails {
                    resortEmailAddress
                    resortPhoneNumber
                    host{
                        firstAndLastName
                        role,
                        picture {
                            url
                        }
                    }
                }
                arrivalRestrictions{
                    information
                    weekdaysWithArrivalRestrictions
                }
                importantNotificationsCollection {
                    items {
                        name
                        notificationType
                        visibleOn
                        visibility
                        visibleFrom
                        visibileTo
                        titleInTheAlertBar
                        contentTitle
                        content {
                            json
                        }
                        sys {
                            id
                        }
                    }
                }
                localChargesType
                seo {
                    title
                    description
                }
                gastronomyInfo
                bookingTermsConditionsText{
                    text {
                        json,
                        links {
                            assets {
                                block {
                                    sys {
                                        id
                                    }
                                }
                                hyperlink {
                                    url
                                }
                            }
                        }
                    }
                }
                reservationSections
                preArrivalImportantInformation
                partnerResortInfo {
                    description {
                        json
                    }
                }
            }
        }
    }
`;

export const useResortDetailBySlug = (slug) => {
    const {localeUppercase, localeUppercaseUnderscore} = useContext(LocaleContext)[0];
    const [resortsStateAndIds, resortsDispatch] = useContext(ResortsContext);
    const {resorts, resortsLoading} = resortsStateAndIds;
    const {logger} = useLogger();
    const {loading, error, data} = useQuery(QUERY, {
        variables: {slug, locale: localeUppercase},
        errorPolicy: "all"
    });

    useEffect(() => {
        if (resorts && !resorts.length && !resortsLoading) {
            resortsDispatch({type: "GET_RESORTS_INIT", payload: {locale: localeUppercaseUnderscore, is_bookable: true}});
        }
    }, [resorts]);


    if (error?.message) {
        logger(error?.message, "error", "useResortDetailBySlug", error?.stack);
    }

    const memoedValue = useMemo(() => {
        const resortDetails = data?.resortCollection?.items[0];
        const resortTags = resortDetails?.resortPositioningCollection?.items?.map(({title}) => title);
        const resortSEOData = resortDetails?.seo;
        const currentResort = resorts?.find(r => r.hapimagResortId === resortDetails?.hapimagResortId);

        return {
            resortDetails: resortDetails ? {
                ...resortDetails,
                expirationDaysBeforeArrival: currentResort?.wlExpirationDaysBeforeArrival,
                isPartner: currentResort?.isPartner,
                resortStayRestrictions: currentResort?.resortStayRestrictions,
            } : undefined,
            resortTags,
            resortSEOData,
            loading,
            error
        };
    }, [
        resorts,
        data?.resortCollection?.items?.[0],
        loading,
        error
    ]);

    return memoedValue;
};
